import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import type { ImpactResult } from '../../data/impactTypes';
import { formatCompact, formatSnapRate, SNAP_SUPPRESSED_NOTE } from '../../data/impactTypes';

import { overviewPanelStyles as styles } from './OverviewPanel.styles';
import { OVERVIEW_METRIC_TOOLTIPS } from './overviewTooltips';
import { InfoTooltip, MetricRow } from './shared';

interface MetricsListProps {
  /** `null` while loading (first paint) or after a failed /impact/result call. */
  result: ImpactResult | null;
  resultLoading: boolean;
  resultError: string | null;
  onRetry: () => void;
}

/**
 * The 7 overview metric rows + "moving the needle" bar — shared by the
 * desktop `OverviewPanel` and the mobile bottom sheet's "Impact Data" tab.
 */
export function MetricsList({ result, resultLoading, resultError, onRetry }: MetricsListProps) {
  if (!result) {
    return (
      <Box sx={styles.statusBox}>
        <Typography sx={resultError ? styles.errorText : styles.emptyHint}>
          {resultError ?? 'Loading impact data…'}
        </Typography>
        {resultError && !resultLoading && (
          <Link component="button" type="button" onClick={onRetry} sx={styles.retryLink}>
            Try again
          </Link>
        )}
      </Box>
    );
  }

  return (
    <>
      <Box sx={styles.metrics}>
        <MetricRow
          label="Eligible children under 18"
          value={formatCompact(result.overview.eligibleChildrenUnder18)}
          tooltip={OVERVIEW_METRIC_TOOLTIPS.eligibleChildrenUnder18}
          emphasize
        />
        <MetricRow
          label="Median household income"
          value={formatCompact(result.overview.medianHouseholdIncome, true)}
          tooltip={OVERVIEW_METRIC_TOOLTIPS.medianHouseholdIncome}
          emphasize
        />
        <MetricRow
          label="529 account participation"
          value={`${result.overview.participation529}%`}
          tooltip={OVERVIEW_METRIC_TOOLTIPS.participation529}
          emphasize
        />
        <MetricRow
          label="Child poverty rate"
          value={`${result.overview.childPovertyRate}%`}
          tooltip={OVERVIEW_METRIC_TOOLTIPS.childPovertyRate}
          emphasize
        />
        <MetricRow
          label="Economic mobility index"
          value={result.overview.economicMobility}
          tooltip={OVERVIEW_METRIC_TOOLTIPS.economicMobility}
          emphasize
        />
        <MetricRow
          label="Children receiving SNAP"
          value={formatSnapRate(result.overview.childrenReceivingSNAP)}
          note={result.overview.childrenReceivingSNAP > 100 ? SNAP_SUPPRESSED_NOTE : undefined}
          tooltip={OVERVIEW_METRIC_TOOLTIPS.childrenReceivingSNAP}
          emphasize
        />
        <MetricRow
          label="Housing burdened"
          value={`${result.overview.housingBurdened}%`}
          tooltip={OVERVIEW_METRIC_TOOLTIPS.housingBurdened}
          emphasize
        />
      </Box>

      <Box sx={styles.needle}>
        <Box sx={styles.needleHead}>
          <Box sx={styles.metricLabel}>
            <Typography component="span" sx={styles.needleLabel}>
              Moving the needle on the savings gap
            </Typography>
            <InfoTooltip tooltip={OVERVIEW_METRIC_TOOLTIPS.needleScore} />
          </Box>
          <Typography component="span" sx={styles.metricValueEmphasis}>
            {result.overview.needleScore}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          // The score itself can exceed 100% (a gift outpacing the expected
          // savings gap) — the label shows the real number, but the bar
          // just fills completely rather than overflowing.
          value={Math.min(result.overview.needleScore, 100)}
          sx={styles.needleBar}
        />
      </Box>
    </>
  );
}
